import { ReactElement, useCallback, useEffect, useState } from 'react'

import { getCarAllowances } from '../../actions/car-allowances'
import { getCoarseCarAllowances } from '../../actions/coarse-car-allowances'
import { getCoarseSalaryRegistrations } from '../../actions/coarse-salary-registrations'
import { getCoarseTimeRegistrations } from '../../actions/coarse-time-registrations'
import { getContractDeltas, getRemuneration } from '../../actions/contracts'
import { getCostCenters } from '../../actions/cost-centers'
import { getLeaveBalances } from '../../actions/leave-balances'
import { getLeaveTypes } from '../../actions/leave-types'
import { getOneTimePayPensions } from '../../actions/one-time-pay-pensions'
import { getOneTimePays } from '../../actions/one-time-pays'
import { getPaySlips } from '../../actions/pay-slips'
import { getPensionCompanies } from '../../actions/pension-companies'
import { getProjects } from '../../actions/projects'
import { getSalaryCycles } from '../../actions/salary-cycles'
import { getSalaryRegistrations } from '../../actions/salary-registrations'
import { getSupplementBalances } from '../../actions/supplement-balances'
import { getTimeRegistrations } from '../../actions/time-registrations'
import { Result as LeaveResult } from '../../components/employees-single/leave/LeaveEditForm'
import { ResultFields as CoarseSalaryRegistrationResult } from '../../components/employees-single/salary-registrations/CoarseSalaryRegistrationForm'
import { ResultFields as DetailedSalaryRegistrationResult } from '../../components/employees-single/salary-registrations/DetailedSalaryRegistrationEditForm'
import { CoarseTimeRegistrationResult } from '../../components/employees-single/time-registration/CoarseTimeRegistrationForm'
import { ResultFields as TimeRegistrationResult } from '../../components/employees-single/time-registration/DetailedTimeRegistrationEditForm'
import Company from '../../model/company'
import { WorkSchedule } from '../../model/contract'
import ContractDelta from '../../model/contractDelta'
import Employee from '../../model/employee'
import SalaryCycle from '../../model/salaryCycle'
import SalaryPeriod from '../../model/salaryPeriod'
import SalaryType from '../../model/salaryType'
import { DateFormat } from '../../model/types'
import { CarAllowanceReducer } from '../../reducers/carAllowances'
import { CoarseCarAllowanceReducer } from '../../reducers/coarseCarAllowances'
import { CoarseSalaryRegistrationReducer } from '../../reducers/coarseSalaryRegistrations'
import { CoarseTimeRegistrationReducer } from '../../reducers/coarseTimeRegistrations'
import { CostCenterReducer } from '../../reducers/costCenters'
import { DepartmentReducer } from '../../reducers/departments'
import { EmployeeContractDeltaReducer } from '../../reducers/employeeContractDeltas'
import { LeaveBalanceReducer } from '../../reducers/leaveBalances'
import { LeaveTypeReducer } from '../../reducers/leaveTypes'
import { OneTimePayPensionReducer } from '../../reducers/oneTimePayPensions'
import { OneTimePayReducer } from '../../reducers/oneTimePays'
import { PaySlipReducer } from '../../reducers/paySlips'
import { PensionCompanyReducer } from '../../reducers/pensionCompanies'
import { ProjectReducer } from '../../reducers/projects'
import { SalaryCycleReducer } from '../../reducers/salaryCycles'
import { SalaryRegistrationReducer } from '../../reducers/salaryRegistrations'
import { SalaryTypeReducer } from '../../reducers/salaryTypes'
import { SupplementBalanceReducer } from '../../reducers/supplementBalances'
import { SupplementTypeReducer } from '../../reducers/supplementTypes'
import { TimeRegistrationReducer } from '../../reducers/timeRegistrations'
import { UserReducer } from '../../reducers/user'
import RemunerationType from '../../types/remuneration-type'
import { formatDate, isSameOrAfter, isSameOrBefore, isTimeBetween } from '../../utils/date-utils'
import { formatLeaveSubType, formatLeaveTypeName } from '../../utils/format-utils'
import { formatCurrency } from '../../utils/number-utils'
import { connectToReducer } from '../../utils/reducer-utils'
import { t } from '../../utils/translation-utils'
import Button from '../elements/button'
import Col from '../elements/grid/col'
import Row from '../elements/grid/row'
import Headline from '../elements/Headline'
import Modal from '../elements/modal'
import Select from '../elements/select'
import UserImage from '../elements/UserImage'
import PayRollAddEmployeePopup, { ResultData } from './PayRollAddEmployeePopup'

export type AdjustmentList = {
  key: string
  type: string
  existing: boolean
  unique: boolean
  text: string | undefined
  unit: string | undefined
  value: number
  adjustment: number
  data: ResultData
}

type Props = {
  employeeID: string
  employee: Employee | undefined
  company: Company
  period: string[]

  chosenSalaryCycle: SalaryCycle | undefined
  chosenSalaryCycleID: string
  chosenPeriod: SalaryPeriod | undefined
  chosenPeriodID: string

  saveData: (id: string, data: AdjustmentList[]) => void
  getAdjustments: (id: string) => AdjustmentList[]
}

type Reducers = {
  salaryCycles: SalaryCycleReducer
  oneTimePays: OneTimePayReducer
  departments: DepartmentReducer
  leaveBalances: LeaveBalanceReducer
  timeRegistrations: TimeRegistrationReducer
  carAllowances: CarAllowanceReducer
  coarseTimeRegistrations: CoarseTimeRegistrationReducer
  coarseSalaryRegistrations: CoarseSalaryRegistrationReducer
  salaryRegistrations: SalaryRegistrationReducer
  supplementBalances: SupplementBalanceReducer
  supplementTypes: SupplementTypeReducer
  leaveTypes: LeaveTypeReducer
  costCenters: CostCenterReducer
  user: UserReducer
  pensionCompanies: PensionCompanyReducer
  oneTimePayPension: OneTimePayPensionReducer
  employeeContractDeltas: EmployeeContractDeltaReducer
  salaryTypes: SalaryTypeReducer
  projects: ProjectReducer
  paySlips: PaySlipReducer
  coarseCarAllowance: CoarseCarAllowanceReducer
}

type Actions = {
  getSalaryCycles: () => void
  getOneTimePays: (companyID: string | undefined, employeeID: string | undefined) => void
  getLeaveBalances: (companyID?: string, employeeID?: string) => void
  getTimeRegistrations: (
    companyID: string | undefined,
    employeeID: string | undefined,
    payRollID: string | undefined,
    fromDate: DateFormat,
    toDate: DateFormat
  ) => void
  getCarAllowances: (
    companyID: string | undefined,
    employeeID: string | undefined,
    fromDate: DateFormat,
    toDate: DateFormat
  ) => void
  getCoarseTimeRegistrations: (
    companyID: string | undefined,
    salaryPeriodID: string | undefined,
    employeeID: string | undefined
  ) => void
  getCoarseSalaryRegistrations: (companyID?: string, salaryPeriodID?: string, employeeID?: string) => void
  getSalaryRegistrations: (
    companyID: string | undefined,
    employeeID: string | undefined,
    payRollID: string | undefined,
    fromDate: DateFormat,
    toDate: DateFormat
  ) => void
  getSupplementBalances: () => void
  getLeaveTypes: () => void
  getCostCenters: () => void
  getPensionCompanies: () => void
  getOneTimePayPensions: (companyID?: string, employeeID?: string) => void
  getContractDeltas: (employeeID: string) => void
  getRemuneration: (contractID: string) => void
  getProjects: () => void
  getPaySlips: (payRollID: string | undefined, employeeID: string | undefined) => void
  getCoarseCarAllowances: (companyID?: string, salaryPeriodIDs?: string[], employeeID?: string) => void
}

type ContractData = {
  validFrom: string
  validTo?: string
  contractID: string
}

export function getTypeToText(): Map<string, string> {
  return new Map<string, string>([
    ['bonus', t('pay_rolls.manual.type.bonus')],
    ['vacation', t('pay_rolls.manual.type.leave')],
    ['reimbursement', t('pay_rolls.manual.type.reimbursement')],
    ['car', t('pay_rolls.manual.type.car')],
    ['car-detailed', t('pay_rolls.manual.type.car')],
    ['travel', t('pay_rolls.manual.type.travel')],
    ['advance', t('pay_rolls.manual.type.advance')],
    ['extra-pension', t('pay_rolls.manual.type.extra_pension')],
    ['salary-reduction', t('pay_rolls.manual.type.salary_reduction')],
    ['time', t('pay_rolls.manual.type.time')],
    ['time-detailed', t('pay_rolls.manual.type.time')],
    ['salary', t('pay_rolls.manual.type.salary')],
    ['salary-detailed', t('pay_rolls.manual.type.salary')],
    ['fee', t('pay_rolls.manual.type.fee')],
  ])
}

function getTypeToUnit(): Map<string, string> {
  return new Map<string, string>([
    ['bonus', t('pay_rolls.manual.unit.kr')],
    ['vacation', t('pay_rolls.manual.unit.days')],
    ['reimbursement', t('pay_rolls.manual.unit.kr')],
    ['car', t('pay_rolls.manual.unit.km')],
    ['car-detailed', t('pay_rolls.manual.unit.km')],
    ['travel', t('pay_rolls.manual.unit.kr')],
    ['advance', t('pay_rolls.manual.unit.kr')],
    ['extra-pension', t('pay_rolls.manual.unit.kr')],
    ['salary-reduction', t('pay_rolls.manual.unit.kr')],
    ['time', t('pay_rolls.manual.unit.hour')],
    ['time-detailed', t('pay_rolls.manual.unit.hour')],
    ['salary', t('pay_rolls.manual.unit.kr')],
    ['salary-detailed', t('pay_rolls.manual.unit.piece')],
    ['fee', t('pay_rolls.manual.unit.kr')],
  ])
}

// Check if form2-to2 overlap with from1-to1
function doPeriodOverlap(from1: string, to1: string, from2: string, to2: string): boolean {
  // if form2-to2 streach past from1-to1 on both side, it overlap
  if (isSameOrBefore(from2, from1) && isSameOrAfter(to2, to1)) {
    return true
  }
  // if from2 is inside form1-to1 it overlaps
  if (isSameOrAfter(from2, from1) && isSameOrBefore(from2, to1)) {
    return true
  }
  //  if to2 is inside form1-to1 it overlaps
  if (isSameOrAfter(to2, from1) && isSameOrBefore(to2, to1)) {
    return true
  }
  return false
}

function newExtAdjust(type: string, amount: number): AdjustmentList {
  return {
    key: Math.random().toString(36).substring(2, 10),
    type: type,
    existing: true,
    unique: ['time', 'car', 'salary'].includes(type),
    text: getTypeToText().get(type),
    unit: getTypeToUnit().get(type),
    value: 0,
    adjustment: amount,
    data: undefined,
  }
}
/*
function newAdjust(type: string, amount: number): AdjustmentList {
  return {
    key: Math.random().toString(36).substring(2, 10),
    type: type,
    existing: false,
    unique: ['time', 'car', 'salary'].includes(type),
    text: getTypeToText().get(type),
    unit: getTypeToUnit().get(type),
    value: 0,
    adjustment: amount,
    data: undefined,
  }
}
*/
// Extracts the salaryTypeID(s) referenced by a row's data, regardless of coarse/detailed shape
function getSalaryTypeIDs(e: AdjustmentList): string[] {
  switch (e.type) {
    case 'salary':
      return Object.keys((e.data as CoarseSalaryRegistrationResult)?.registrations || {})
    case 'salary-detailed': {
      const id = (e.data as DetailedSalaryRegistrationResult)?.salaryTypeID
      return id ? [id] : []
    }
    case 'time':
      return Object.keys((e.data as CoarseTimeRegistrationResult)?.workHours || {})
    case 'time-detailed': {
      const id = (e.data as TimeRegistrationResult)?.salaryTypeID
      return id ? [id] : []
    }
    default:
      return []
  }
}

function PayRollAddSingleEmployee(props: Actions & Reducers & Props): ReactElement | null {
  const {
    salaryCycles,
    getSalaryCycles,
    oneTimePays,
    getOneTimePays,
    employeeID,
    leaveBalances,
    getTimeRegistrations,
    timeRegistrations,
    carAllowances,
    getCarAllowances,
    coarseTimeRegistrations,
    getCoarseTimeRegistrations,
    coarseSalaryRegistrations,
    getCoarseSalaryRegistrations,
    salaryRegistrations,
    getSalaryRegistrations,
    supplementBalances,
    getSupplementBalances,
    leaveTypes,
    getLeaveTypes,
    costCenters,
    getCostCenters,
    pensionCompanies,
    getPensionCompanies,
    employeeContractDeltas,
    oneTimePayPension,
    getOneTimePayPensions,
    getContractDeltas,
    getRemuneration,
    salaryTypes,
    projects,
    getProjects,
    getAdjustments,
    paySlips,
    getPaySlips,
    coarseCarAllowance,
    getCoarseCarAllowances,
  } = props

  const [chosenEmployee, setChosenEmployee] = useState('')

  const [bonusAmount, setBonusAmount] = useState(0)
  const [vacationAmount, setVacationAmount] = useState(0)
  const [carAmount, setCarAmount] = useState(0)
  const [travelAmount, setTravelAmount] = useState(0)
  const [reimbAmount, setReimbAmount] = useState(0)
  const [advanceAmount, setAdvanceAmount] = useState(0)
  const [hourAmount, setHourAmount] = useState(0)
  const [feeAmount, setFeeAmount] = useState(0)

  const [popContent, setPopContent] = useState('')
  const [adjustKey, setAdjustKey] = useState('')
  const [oldValue, setOldValue] = useState<ResultData>()

  const [periodContract, setPeriodContract] = useState<ContractDelta>()
  const [noContract, setNoContract] = useState<boolean>(false)
  const [baseRate, setBaseRate] = useState<number>(0.0)

  const [chosenType, setChosenType] = useState('')
  const [adjustmentList, setAdjustmentList] = useState<AdjustmentList[]>([])

  const [disableType, setDisableType] = useState<string[]>([])
  const [modalVisible, setModalVisible] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const testInPeriod = useCallback(
    (date: string): boolean => {
      return isTimeBetween(date, props.period[0], props.period[1])
    },
    [props.period]
  )

  function formatValue(value: number, unit: string): string {
    switch (unit) {
      case t('pay_rolls.manual.unit.kr'):
        return formatCurrency(value)
      default:
        return value.toFixed(2) + ' ' + unit
    }
  }

  function getAdjustmentDates(e: AdjustmentList): string {
    if (e.type === 'salary-detailed') {
      const d = e.data as DetailedSalaryRegistrationResult
      return d ? formatDate(d.date) : ''
    }
    if (e.type === 'time-detailed') {
      const d = e.data as TimeRegistrationResult
      return d ? formatDate(d.date) : ''
    }
    if (e.type !== 'vacation') return ''
    if (e.existing === false) {
      const d = e.data as LeaveResult
      if (!d) return ''
      return d.startDate === d.endDate
        ? formatDate(d.startDate)
        : `${formatDate(d.startDate)} - ${formatDate(d.endDate)}`
    }
    // aggregated pre-existing row: derive a range from the same filter used for vacationAmount
    const leaveDates = timeRegistrations.timeRegistrations
      .filter((r) => r.class === 'Leave' && testInPeriod(r.date))
      .map((r) => r.date)
      .toArray()
    if (leaveDates.length === 0) return ''
    const sorted = [...leaveDates].sort()
    const first = sorted[0]
    const last = sorted[sorted.length - 1]
    return first === last ? formatDate(first) : `${formatDate(first)} - ${formatDate(last)}`
  }

  function getAdjustmentSubType(e: AdjustmentList): string {
    if (e.type === 'vacation') {
      if (e.existing === false) {
        const d = e.data as LeaveResult
        if (!d) return ''
        const leaveType = leaveTypes.leaveTypes.find((lt) => lt.id === d.leaveTypeID)
        const parts = [
          leaveType ? formatLeaveTypeName(leaveType.name) : '',
          d.leaveSubTypeID ? formatLeaveSubType(d.leaveSubTypeID) : '',
        ].filter(Boolean)
        return parts.join(' - ')
      }
      const leaveRegs = timeRegistrations.timeRegistrations
        .filter((r) => r.class === 'Leave' && testInPeriod(r.date))
        .toArray()
      const ids = new Set(leaveRegs.map((r) => r.leaveTypeID))
      if (ids.size === 0) return ''
      if (ids.size > 1) return t('pay_rolls.manual.sub_type.multiple')
      const leaveType = leaveTypes.leaveTypes.find((lt) => lt.id === leaveRegs[0].leaveTypeID)
      return leaveType ? formatLeaveTypeName(leaveType.name) : ''
    }
    if (e.type === 'salary' || e.type === 'salary-detailed' || e.type === 'time' || e.type === 'time-detailed') {
      const salaryTypeIDs = getSalaryTypeIDs(e)
      const names = salaryTypeIDs
        .map((id) => salaryTypes.salaryTypes.find((st) => st.id === id))
        .filter((st): st is SalaryType => !!st)
        .map((st) => st.title || st.name)
      if (names.length > 0) return names.join(', ')
      // fallback: no resolvable salaryTypeID on this row's data, show the registration method instead
      const methodType =
        e.type === 'salary' || e.type === 'salary-detailed'
          ? periodContract?.contract.salaryRegistrationMethodType
          : periodContract?.contract.timeRegistrationMethodType
      return methodType === 'Coarse'
        ? t('contract_delta.field.time_registration_method_type.coarse')
        : t('contract_delta.field.time_registration_method_type.detailed')
    }
    return ''
  }

  useEffect(() => {
    // find the active contract for the given period.
    // If there are more than one contract for the period choose the newest.
    let contractData: ContractData[] = []
    const comparator = (a: ContractData, b: ContractData) => a.validFrom.localeCompare(b.validFrom)
    contractData = []
    for (const c of employeeContractDeltas.contracts) {
      const elm: ContractData = {
        validFrom: c.validFrom,
        validTo: c.validTo ? c.validTo : undefined,
        contractID: c.contractID,
      }
      if (!c.validTo) {
        if (isSameOrBefore(c.validFrom, props.period[1])) {
          contractData.push(elm)
        }
        continue
      }
      if (doPeriodOverlap(props.period[0], props.period[1], c.validFrom, c.validTo)) {
        contractData.push(elm)
      }
    }
    if (contractData.length === 0) {
      setNoContract(true)
      return
    }
    setNoContract(false)
    contractData = contractData.sort(comparator)
    const c = employeeContractDeltas.contracts.find((d) => d.contractID == contractData[0].contractID)
    setPeriodContract(c)
  }, [employeeContractDeltas, props.period])

  useEffect(() => {
    // load data for the chosen employee
    if (employeeID !== chosenEmployee) {
      setChosenEmployee(employeeID)
      getLeaveBalances(props.company.id, employeeID)
      // /v2/timeRegistrations ignores employeeID when companyID is also present and returns
      // every employee's registrations instead, so this call must omit companyID.
      getTimeRegistrations(undefined, employeeID, undefined, props.period[0], props.period[1])
      getCarAllowances(undefined, employeeID, props.period[0], props.period[1])
      // /v2/coarseTimeRegistrations ignores employeeID when companyID (or salaryPeriodID) is also
      // present and returns every employee's registrations instead, so this call must omit them.
      getCoarseTimeRegistrations(undefined, undefined, employeeID)
      getCoarseSalaryRegistrations(undefined, props.chosenPeriodID, employeeID)
      getSalaryRegistrations(undefined, employeeID, undefined, props.period[0], props.period[1])
      getOneTimePays(undefined, employeeID)
      getSupplementBalances()
      getLeaveTypes()
      getCostCenters()
      getPensionCompanies()
      getContractDeltas(employeeID)
      getPaySlips(undefined, employeeID)
      getOneTimePayPensions(props.company.id, employeeID)
      getCoarseCarAllowances(props.company.id, [props.chosenPeriodID], employeeID)
      return
    }

    function test(obj: any): boolean {
      return !obj.loading && !obj.loaded && !obj.error
    }

    if (test(salaryCycles)) {
      getSalaryCycles()
    }

    if (!oneTimePays.loading && !oneTimePays.loaded && !oneTimePays.error) {
      getOneTimePays(undefined, employeeID)
    }

    if (!timeRegistrations.loading && !timeRegistrations.loaded && !timeRegistrations.error) {
      // /v2/timeRegistrations ignores employeeID when companyID is also present and returns
      // every employee's registrations instead, so this call must omit companyID.
      getTimeRegistrations(undefined, employeeID, undefined, props.period[0], props.period[1])
    }

    if (!carAllowances.loading && !carAllowances.loaded && !carAllowances.error) {
      getCarAllowances(undefined, employeeID, props.period[0], props.period[1])
    }

    if (!coarseTimeRegistrations.loading && !coarseTimeRegistrations.loaded && !coarseTimeRegistrations.error) {
      getCoarseTimeRegistrations(undefined, undefined, employeeID)
    }

    if (!coarseSalaryRegistrations.loading && !coarseSalaryRegistrations.loaded && !coarseSalaryRegistrations.error) {
      getCoarseSalaryRegistrations(undefined, props.chosenPeriodID, employeeID)
    }

    if (!salaryRegistrations.loading && !salaryRegistrations.loaded && !salaryRegistrations.error) {
      getSalaryRegistrations(undefined, employeeID, undefined, props.period[0], props.period[1])
    }

    if (!supplementBalances.loading && !supplementBalances.loaded && !supplementBalances.error) {
      getSupplementBalances()
    }

    if (!leaveBalances.loading && !leaveBalances.loaded && !leaveBalances.error) {
      getLeaveBalances('', employeeID)
    }

    if (!leaveTypes.loading && !leaveTypes.loaded && !leaveTypes.error) {
      getLeaveTypes()
    }

    if (!costCenters.loading && !costCenters.loaded && !costCenters.error) {
      getCostCenters()
    }

    if (!oneTimePayPension.loading && !oneTimePayPension.loaded && !oneTimePayPension.error) {
      getOneTimePayPensions()
    }

    if (!pensionCompanies.loading && !pensionCompanies.loaded && !pensionCompanies.error) {
      getPensionCompanies()
    }

    if (!projects.loading && !projects.loaded && !projects.error) {
      getProjects()
    }

    if (!employeeContractDeltas.loading && !employeeContractDeltas.loaded && !employeeContractDeltas.error) {
      getContractDeltas(employeeID)
    }

    if (!paySlips.loading && !paySlips.loaded && !paySlips.error) {
      getPaySlips(undefined, employeeID)
    }

    if (!coarseCarAllowance.loaded && !coarseCarAllowance.loaded && !coarseCarAllowance.error) {
      getCoarseCarAllowances(props.company.id, [props.chosenPeriodID], employeeID)
    }

    if (props.employee && props.employee.activeContract && !props.employee.activeContract.remuneration) {
      getRemuneration(props.employee.activeContract.id)
    }
  }, [
    props.period,
    salaryCycles,
    getSalaryCycles,
    getOneTimePays,
    oneTimePays,
    employeeID,
    chosenEmployee,
    leaveBalances,
    props.company,
    timeRegistrations,
    getTimeRegistrations,
    carAllowances,
    getCarAllowances,
    coarseTimeRegistrations,
    getCoarseTimeRegistrations,
    coarseSalaryRegistrations,
    getCoarseSalaryRegistrations,
    salaryRegistrations,
    getSalaryRegistrations,
    supplementBalances,
    getSupplementBalances,
    leaveTypes,
    getLeaveTypes,
    costCenters,
    getCostCenters,
    pensionCompanies,
    getPensionCompanies,
    employeeContractDeltas,
    oneTimePayPension,
    getOneTimePayPensions,
    getContractDeltas,
    getRemuneration,
    projects,
    getProjects,
    paySlips,
    getPaySlips,
    getCoarseCarAllowances,
    coarseCarAllowance,
    props.employee,
    props.chosenPeriodID,
  ])

  // Set adjustment list for employee
  useEffect(() => {
    const adjust = getAdjustments(employeeID)
    if (adjust.length !== 0) {
      setAdjustmentList(adjust)
    }
  }, [employeeID, getAdjustments])

  useEffect(() => {
    const reducers = [
      salaryCycles,
      oneTimePays,
      timeRegistrations,
      carAllowances,
      supplementBalances,
      leaveTypes,
      costCenters,
      pensionCompanies,
      employeeContractDeltas,
      paySlips,
    ]
    if (reducers.some((r) => r.error)) {
      setLoadError(true)
    } else {
      setLoadError(false)
    }

    if (reducers.every((r) => r.loaded)) {
      setIsLoaded(true)
    } else {
      setIsLoaded(false)
    }
  }, [
    salaryCycles,
    oneTimePays,
    leaveBalances,
    timeRegistrations,
    carAllowances,
    coarseTimeRegistrations,
    supplementBalances,
    leaveTypes,
    costCenters,
    pensionCompanies,
    employeeContractDeltas,
    projects,
    paySlips,
  ])

  useEffect(() => {
    // paySlips and employeeContractDeltas are singleton reducers shared across employees; only
    // trust them once they have actually loaded data for the employee currently being viewed,
    // otherwise a fresh mount or employee switch can briefly read stale/empty data left over
    // from the previously viewed employee, causing baseRate to be set from an incomplete picture
    // and never reset once the real (settled) data arrives.
    if (!paySlips.loaded || paySlips.employeeID !== employeeID) return
    if (!employeeContractDeltas.loaded || employeeContractDeltas.employeeID !== employeeID) return

    // see if there is a payslip for the chosenPeriod
    let isSetteled = false
    paySlips.paySlips.forEach((e) => {
      if (e.salaryPeriodID == props.chosenPeriodID) {
        if (e.settled) {
          isSetteled = true
        }
      }
    })

    if (isSetteled) {
      setBaseRate(0)
      return
    }

    // Find the contract that was valid for the chosen period (using props.chosenPeriod?.dispositionDate)
    let rate = 0
    if (props.chosenPeriod) {
      const dispDate = props.chosenPeriod.dispositionDate
      employeeContractDeltas.contracts.forEach((e) => {
        if (isSameOrBefore(e.validFrom, dispDate)) {
          if (!e.validTo || isSameOrBefore(e.validTo, dispDate)) {
            if (e.contract.remuneration) {
              e.contract.remuneration.salary.forEach((s) => {
                rate = s.rate
              })
            }
          }
        }
      })
    }
    setBaseRate(rate)
  }, [paySlips, employeeContractDeltas, props.chosenPeriodID, props.chosenPeriod, employeeID])

  useEffect(() => {
    // oneTimePays is a singleton reducer shared across employees; only trust it once it has
    // actually loaded data for the employee currently being viewed, otherwise a fresh mount
    // can briefly read stale data left over from the previously viewed employee.
    if (!oneTimePays.loaded || oneTimePays.employeeID !== employeeID) return
    let am = 0 // Bonus
    let tr = 0 // travel
    let re = 0 // reimbursment
    let ad = 0 // pay advance
    let fe = 0 // fee
    oneTimePays.oneTimePays.forEach((e) => {
      if (testInPeriod(e.dispositionDate) && e.type === 'Bonus') {
        am += e.amount
      }
      if (testInPeriod(e.dispositionDate) && e.type === 'Travel Allowance') {
        tr += e.amount
      }

      if (testInPeriod(e.dispositionDate) && e.type === 'Reimbursement') {
        re += e.amount
      }

      if (testInPeriod(e.dispositionDate) && e.type === 'Pay Check Advanced') {
        ad += e.amount
      }

      if (testInPeriod(e.dispositionDate) && ['Compensation', 'Honorary Gift', 'Compensation No AM'].includes(e.type)) {
        fe += e.amount
      }
    })
    setBonusAmount(am)
    setTravelAmount(tr)
    setReimbAmount(re)
    setAdvanceAmount(ad)
    setFeeAmount(fe)
  }, [oneTimePays, testInPeriod, employeeID])

  useEffect(() => {
    if (!oneTimePays.loaded || oneTimePays.employeeID !== employeeID) return
    const adjust: AdjustmentList[] = []
    oneTimePays.oneTimePays.forEach((e) => {
      if (!testInPeriod(e.dispositionDate)) {
        return
      }
      if (e.type === 'Bonus') {
        adjust.push(newExtAdjust('bonus', e.amount))
      }
      if (e.type === 'Travel Allowance') {
        adjust.push(newExtAdjust('travel', e.amount))
      }
      if (e.type === 'Reimbursement') {
        adjust.push(newExtAdjust('reimbursement', e.amount))
      }
      if (e.type === 'Pay Check Advanced') {
        adjust.push(newExtAdjust('advance', e.amount))
      }
      if (e.type === 'Gross Pay Reduction') {
        adjust.push(newExtAdjust('salary-reduction', e.amount))
      }
      if (['Compensation', 'Honorary Gift', 'Compensation No AM'].includes(e.type)) {
        adjust.push(newExtAdjust('fee', e.amount))
      }
    })
    if (adjust.length === 0) return
    setAdjustmentList((prev) => {
      const existingBonusPresent = prev.some((e) => e.type === 'bonus' && e.existing === true)
      const existingReimbursementPresent = prev.some((e) => e.type === 'reimbursement' && e.existing === true)
      const existingAdvancePresent = prev.some((e) => e.type === 'advance' && e.existing === true)
      const existingTravelPresent = prev.some((e) => e.type === 'travel' && e.existing === true)
      const existingReductionPresent = prev.some((e) => e.type === 'salary-reduction' && e.existing === true)
      const existingFeePresent = prev.some((e) => e.type === 'fee' && e.existing === true)

      const bonusToAdd = existingBonusPresent ? [] : adjust.filter((e) => e.type === 'bonus')
      const reimbToAdd = existingReimbursementPresent ? [] : adjust.filter((e) => e.type === 'reimbursement')
      const advanceToAdd = existingAdvancePresent ? [] : adjust.filter((e) => e.type === 'advance')
      const travelToAdd = existingTravelPresent ? [] : adjust.filter((e) => e.type === 'travel')
      const reductionToAdd = existingReductionPresent ? [] : adjust.filter((e) => e.type === 'salary-reduction')
      const feeToAdd = existingFeePresent ? [] : adjust.filter((e) => e.type === 'fee')
      const toAdd = [...bonusToAdd, ...reimbToAdd, ...advanceToAdd, ...travelToAdd, ...reductionToAdd, ...feeToAdd]
      if (toAdd.length === 0) return prev
      return [...toAdd, ...prev]
    })
  }, [oneTimePays, testInPeriod, employeeID])

  useEffect(() => {
    // timeRegistrations is a singleton reducer shared across employees; only trust it once it
    // has actually loaded data for the employee currently being viewed, otherwise a fresh mount
    // can briefly read stale data left over from the previously viewed employee, and that stale
    // row would then get stuck (the guard below only inserts once per employee view).
    if (!timeRegistrations.loaded || timeRegistrations.employeeID !== employeeID) return
    let vac = 0
    timeRegistrations.timeRegistrations.forEach((e) => {
      if (e.class === 'Leave' && typeof e.days === 'number' && testInPeriod(e.date)) {
        vac += e.days
      }
    })
    setVacationAmount(vac)
    setAdjustmentList((prev) => {
      if (vac === 0) return prev
      if (prev.some((e) => e.type === 'vacation' && e.existing === true)) return prev
      const e = {
        key: Math.random().toString(36).substring(2, 10),
        type: 'vacation',
        existing: true,
        unique: false,
        text: getTypeToText().get('vacation'),
        unit: getTypeToUnit().get('vacation'),
        value: 0,
        adjustment: vac,
        data: undefined,
      }
      return [e, ...prev]
    })
  }, [timeRegistrations, employeeID, testInPeriod])

  useEffect(() => {
    if (!carAllowances.loaded || carAllowances.employeeID !== employeeID) return
    let km = 0
    carAllowances.carAllowances.forEach((e) => {
      km += e.kilometers
    })
    setCarAmount(km)
  }, [carAllowances, employeeID])

  useEffect(() => {
    // insert extra pension if any in the period
    if (!oneTimePayPension.loaded || oneTimePayPension.employeeID !== employeeID) return
    let totalPension = 0
    oneTimePayPension.oneTimePayPensions.forEach((e) => {
      const dispDate = e.oneTimePay.dispositionDate
      if (isSameOrAfter(dispDate, props.period[0]) && isSameOrBefore(dispDate, props.period[1])) {
        totalPension += e.oneTimePay.amount
      }
    })

    setAdjustmentList((prev) => {
      if (totalPension === 0) return prev
      if (prev.some((e) => e.type === 'extra-pension' && e.existing === true)) return prev
      const e = {
        key: Math.random().toString(36).substring(2, 10),
        type: 'extra-pension',
        existing: true,
        unique: false,
        text: getTypeToText().get('extra-pension'),
        unit: getTypeToUnit().get('extra-pension'),
        value: 0,
        adjustment: totalPension,
        data: undefined,
      }
      return [e, ...prev]
    })
  }, [oneTimePayPension, props.period, employeeID])

  useEffect(() => {
    // if coarse car allowance is used there can only be one entry,
    // so insert an interactive entity if there is no entity already.
    if (periodContract?.contract.carAllowanceRegistrationMethodType !== 'Coarse') return
    if (!coarseCarAllowance.loaded || coarseCarAllowance.employeeID !== employeeID) return
    // getCoarseCarAllowances ignores salaryPeriodIDs when employeeID is also passed, returning
    // every period's coarse car allowance for the employee - filter down to the chosen period.
    const forPeriod = coarseCarAllowance.coarseCarAllowances.find((e) => e.salaryPeriodID === props.chosenPeriodID)
    setAdjustmentList((prev) => {
      if (adjustmentList.some((e) => e.type == 'car')) return prev
      if (!forPeriod) return prev
      const e = newExtAdjust('car', forPeriod.kilometers)
      e.data = {
        kilometers: forPeriod.kilometers,
      }
      return [...prev, e]
    })
  }, [periodContract, adjustmentList, coarseCarAllowance, employeeID, props.chosenPeriodID])

  useEffect(() => {
    // detailed car allowance.
    if (periodContract?.contract.carAllowanceRegistrationMethodType !== 'Detailed') return
    if (!carAllowances.loaded || carAllowances.employeeID !== employeeID) return
    let km = 0
    carAllowances.carAllowances.forEach((e) => {
      if (isSameOrAfter(e.date, props.period[0]) && isSameOrBefore(e.date, props.period[1])) {
        km += e.kilometers
      }
    })
    setAdjustmentList((prev) => {
      if (km === 0) return prev
      if (adjustmentList.some((e) => e.type == 'car-detailed')) return prev
      const addKm = newExtAdjust('car-detailed', km)
      return [addKm, ...prev]
    })
  }, [periodContract, carAllowances, adjustmentList, props.period, employeeID])

  useEffect(() => {
    // get existing coarseTimeRegistrations
    if (periodContract?.contract.timeRegistrationMethodType !== 'Coarse') return
    if (!coarseTimeRegistrations.loaded || coarseTimeRegistrations.employeeID !== employeeID) return
    const hours: Record<string, number> = {}
    let totHours = 0
    let days = 0
    // getCoarseTimeRegistrations ignores salaryPeriodIDs when employeeID is also passed, returning
    // every period's coarse time registrations for the employee - filter down to the chosen period.
    coarseTimeRegistrations.coarseTimeRegistrations
      .filter((e) => e.salaryPeriodID === props.chosenPeriodID)
      .forEach((e) => {
        hours[e.salaryTypeID] = e.hours
        totHours += e.hours
        if (e.days !== 0) {
          days = e.days
        }
      })
    setAdjustmentList((prev) => {
      if (prev.some((e) => e.type === 'time')) return prev
      if (Object.entries(hours).length === 0) return prev
      const adjust = {
        key: Math.random().toString(36).substring(2, 10),
        type: 'time',
        existing: true,
        unique: true,
        text: getTypeToText().get('time'),
        unit: getTypeToUnit().get('time'),
        value: 0,
        adjustment: totHours,
        data: {
          workHours: hours,
          workDays: days,
          workSchedule: 'None' as WorkSchedule,
        },
      }
      return [...prev, adjust]
    })
  }, [coarseTimeRegistrations, periodContract, employeeID, props.chosenPeriodID])

  useEffect(() => {
    if (periodContract?.contract.salaryRegistrationMethodType !== 'Detailed') return
    if (!salaryRegistrations.loaded || salaryRegistrations.employeeID !== employeeID) return
    setAdjustmentList((prev) => {
      if (prev.some((e) => e.type === 'salary-detailed' && e.existing === true)) return prev
      const toAdd: AdjustmentList[] = []
      salaryRegistrations.salaryRegistrations.forEach((e) => {
        if (!testInPeriod(e.date)) return
        const entry = newExtAdjust('salary-detailed', e.quantity)
        entry.data = {
          date: e.date,
          quantity: e.quantity,
          salaryTypeID: e.salaryTypeID,
          costCenterID: e.costCenterID,
          note: e.note,
        }
        toAdd.push(entry)
      })
      if (toAdd.length === 0) return prev
      return [...prev, ...toAdd]
    })
  }, [periodContract, salaryRegistrations, testInPeriod, employeeID])

  useEffect(() => {
    if (periodContract?.contract.timeRegistrationMethodType !== 'Detailed') return
    if (!timeRegistrations.loaded || timeRegistrations.employeeID !== employeeID) return
    setAdjustmentList((prev) => {
      if (prev.some((e) => e.type === 'time-detailed' && e.existing === true)) return prev
      const toAdd: AdjustmentList[] = []
      timeRegistrations.timeRegistrations.forEach((e) => {
        if (e.class === 'Leave') return
        if (!testInPeriod(e.date)) return
        const entry = newExtAdjust('time-detailed', e.hours || 0)
        entry.data = {
          class: e.class,
          date: e.date,
          costCenterID: e.costCenterID,
          start: e.start,
          end: e.end,
          hours: e.hours,
          minutes: e.minutes,
          salaryTypeID: e.salaryTypeID,
          projectID: e.projectID,
          note: e.note,
        }
        toAdd.push(entry)
      })
      if (toAdd.length === 0) return prev
      return [...prev, ...toAdd]
    })
  }, [periodContract, timeRegistrations, testInPeriod, employeeID])

  useEffect(() => {
    // get existing coarse salary regestrations
    if (periodContract?.contract.salaryRegistrationMethodType !== 'Coarse') return
    if (!coarseSalaryRegistrations.loaded || coarseSalaryRegistrations.employeeID !== employeeID) return
    type ResultRegistrationFields = {
      quantity: number
      rate: number
    }
    let total = 0
    const regs: Record<string, ResultRegistrationFields> = {}
    coarseSalaryRegistrations.coarseSalaryRegistrations.forEach((e) => {
      let rate = 0
      if (periodContract && periodContract.contract.remuneration) {
        for (const s of periodContract.contract.remuneration.salary) {
          if (s.salaryTypeID === e.salaryTypeID) {
            rate = s.rate
            break
          }
        }
      }
      total += e.quantity * rate
      regs[e.salaryTypeID] = {
        quantity: e.quantity,
        rate: rate,
      }
    })

    setAdjustmentList((prev) => {
      if (prev.some((e) => e.type === 'salary')) return prev
      if (Object.entries(regs).length === 0) return prev
      const adjust = {
        key: Math.random().toString(36).substring(2, 10),
        type: 'salary',
        existing: true,
        unique: true,
        text: getTypeToText().get('salary'),
        unit: getTypeToUnit().get('salary'),
        value: 0,
        adjustment: total,
        data: {
          registrations: regs,
        },
      }
      return [...prev, adjust]
    })
  }, [periodContract, coarseSalaryRegistrations, employeeID])

  useEffect(() => {
    if (!coarseTimeRegistrations.loaded || coarseTimeRegistrations.employeeID !== employeeID) return
    let hours = 0
    coarseTimeRegistrations.coarseTimeRegistrations
      .filter((e) => e.salaryPeriodID === props.chosenPeriodID)
      .forEach((e) => {
        hours += e.hours
      })
    setHourAmount(hours)
  }, [coarseTimeRegistrations, employeeID, props.chosenPeriodID])

  // Populate values in adjustmentList
  useEffect(() => {
    const list: AdjustmentList[] = []
    let updated = false
    adjustmentList.map((v) => {
      const o: AdjustmentList = v
      switch (v.type) {
        case 'bonus':
          if (v.value !== bonusAmount) {
            o.value = bonusAmount
            updated = true
          }
          break
        case 'vacation':
          if (v.value !== vacationAmount) {
            o.value = vacationAmount
            updated = true
          }
          break
        case 'reimbursement':
          if (v.value !== reimbAmount) {
            o.value = reimbAmount
            updated = true
          }
          break
        case 'car':
          if (v.value !== carAmount) {
            o.value = carAmount
            updated = true
          }
          break
        case 'travel':
          if (v.value !== travelAmount) {
            o.value = travelAmount
            updated = true
          }
          break
        case 'advance':
          if (v.value !== advanceAmount) {
            o.value = advanceAmount
            updated = true
          }
          break
        case 'time-detailed':
          if (v.value !== hourAmount) {
            o.value = hourAmount
            updated = true
          }
          break
        case 'fee':
          if (v.value !== feeAmount) {
            o.value = feeAmount
            updated = true
          }
      }

      list.push(o)
    })
    if (updated) {
      setAdjustmentList(list)
    }
  }, [
    adjustmentList,
    bonusAmount,
    vacationAmount,
    reimbAmount,
    carAmount,
    travelAmount,
    advanceAmount,
    hourAmount,
    feeAmount,
  ])

  const [ignore, setIgnore] = useState<Set<string>>(new Set<string>())
  useEffect(() => {
    // Depending on the contract, not all adjustments should be avaliable.
    // The ones that are not, are put on a ignore list so they can be skipped in the dropdown.
    const igSet: Set<string> = new Set<string>()
    const curSalary = periodContract?.contract?.remuneration?.salary
    const curTypes = props.salaryTypes.salaryTypes
    if (curSalary && curTypes) {
      let found = false
      for (const s of curSalary) {
        const i = curTypes.findIndex((t) => t.id === s.salaryTypeID)
        if (i !== -1 && curTypes.get(i)?.class === 'SupplementVaried') {
          found = true
        }
      }
      if (!found) {
        igSet.add('salary')
      }
    }

    if (periodContract?.contract && periodContract?.contract?.remunerationType !== RemunerationType.HOURLY) {
      igSet.add('time')
    }

    // Freelancers are only paid via fee, reimbursement, advance and salary-reduction adjustments -
    // the rest only make sense for ordinary employment contracts. Conversely, fee only makes
    // sense for freelancers.
    if (periodContract?.contract?.employmentType === 'Freelance') {
      const freelancerIgnored = ['bonus', 'vacation', 'car', 'travel', 'extra-pension', 'time', 'salary']
      freelancerIgnored.forEach((k) => igSet.add(k))
    } else {
      igSet.add('fee')
    }
    setIgnore(igSet)
  }, [periodContract, props.salaryTypes])

  useEffect(() => {
    // The types 'car', 'time' and 'salary' can only have one entry into the adjustment list
    // so if there are one of those in the list the option to choose it on the drupdown list
    // should be greyed out.
    const except: string[] = []
    adjustmentList.forEach((v) => {
      if (['car', 'time', 'salary'].indexOf(v.type) != -1) {
        except.push(v.type)
      }
    })
    setDisableType(except)
  }, [adjustmentList])

  function addAdjustment(type: string, amount: number, key: string, data: ResultData) {
    setPopContent('')
    setModalVisible(false)

    const idx = adjustmentList.findIndex((e) => e.key === key)
    if (idx !== -1) {
      adjustmentList[idx].adjustment = amount
      adjustmentList[idx].data = data
      setAdjustmentList(adjustmentList)
    } else {
      const row: AdjustmentList = {
        key: key,
        type: type,
        existing: false,
        unique: ['time', 'salary', 'car'].includes(type),
        text: getTypeToText().get(chosenType),
        unit: getTypeToUnit().get(chosenType),
        value: 0,
        adjustment: amount,
        data: data,
      }
      setAdjustmentList([...adjustmentList, row])
    }
  }

  function chooseType(val: any) {
    setChosenType(val)
  }

  function formPopup(type: string, key: string) {
    const e = adjustmentList.find((a) => a.key === key)
    setPopContent(type)
    if (e) {
      setOldValue(e.data)
    }
    setAdjustKey(key)
    setModalVisible(true)
  }

  function insertRow() {
    const key = Math.random().toString(36).substring(2, 10)
    setOldValue(undefined)
    formPopup(chosenType, key)
  }

  function deleteRow(key: string) {
    setAdjustmentList(adjustmentList.filter((e) => e.key !== key))
  }

  function save() {
    props.saveData(props.employeeID, adjustmentList)
  }

  if (loadError) {
    return (
      <>
        <Row>
          <Col span={12}>
            <h1>{t('pay_rolls.manual.load_error')}</h1>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Button onClick={save} size="extra-extra-large" type="primary">
              {t('pay_rolls.manual.button.back')}
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  if (!isLoaded) {
    return <h1>{t('pay_rolls.manual.loading_data')}</h1>
  }

  if (noContract) {
    return (
      <>
        <Row>
          <Col span={12}>
            <h1>{t('pay_rolls.manual.no_contract_for_period', { name: props.employee?.name })}</h1>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Button onClick={save} size="extra-extra-large" type="primary">
              {t('pay_rolls.manual.button.back')}
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <>
      <Row style={{ display: 'flex', alignItems: 'center' }}>
        <Col span={12}>
          <Row>
            <Col span={21}>
              <Headline style={{ display: 'flex', alignItems: 'center' }}>
                <UserImage src={props.employee?.profileImageURL} name={props.employee?.name || props.employee?.email} />
                <span>{props.employee?.name || props.employee?.email}</span>
              </Headline>
            </Col>
          </Row>
        </Col>
        <Col>
          <h2>
            {t('pay_rolls.manual.period')}: {formatDate(props.period[0])} - {formatDate(props.period[1])}
          </h2>
        </Col>
      </Row>
      {baseRate !== 0 && (
        <Row>
          <Col span={12}>
            <span>
              {t('pay_rolls.manual.unpayed_basis_salary')}: <b>{formatCurrency(baseRate)}</b>
            </span>
          </Col>
        </Row>
      )}
      <Row>
        <Col span={12}>
          <Select placeholder="vælg type" onChange={chooseType}>
            {[...getTypeToText()].map(([k, v]) => {
              if (!ignore.has(k) && k.search(/-detailed$/) === -1) {
                return (
                  <Select.Option key={k} value={k} disabled={disableType.indexOf(k) !== -1}>
                    {v}
                  </Select.Option>
                )
              }
            })}
          </Select>
        </Col>
        <Col span={6}>
          <Button size="large" type="primary" disabled={chosenType === ''} onClick={insertRow}>
            {t('pay_rolls.manual.button.make_adjustment')}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <b>{t('pay_rolls.manual.table_head.type')}</b>
        </Col>
        <Col span={4}>
          <b>{t('pay_rolls.manual.table_head.sub_type')}</b>
        </Col>
        <Col span={4}>
          <b>{t('pay_rolls.manual.table_head.dates')}</b>
        </Col>
        <Col span={6}>
          <b>{t('pay_rolls.manual.table_head.adjustment')}</b>
        </Col>
        <Col span={6}>
          <b>{t('pay_rolls.manual.table_head.action')}</b>
        </Col>
      </Row>
      {adjustmentList.map((e) => {
        return (
          <Row align="center" key={e.key}>
            <Col span={4}>
              <span>{e.text}</span>
            </Col>
            <Col span={4}>
              <span>{getAdjustmentSubType(e) || '-'}</span>
            </Col>
            <Col span={4}>
              <span>{getAdjustmentDates(e) || '-'}</span>
            </Col>
            <Col span={6}>
              <span>{formatValue(e.adjustment, e.unit || '')}</span>
            </Col>
            <Col span={6}>
              {e.existing === false ? (
                <>
                  <Button onClick={() => formPopup(e.type, e.key)}>{t('pay_rolls.manual.button.adjust')}</Button>
                  <Button danger onClick={() => deleteRow(e.key)}>
                    {t('pay_rolls.manual.button.delete')}
                  </Button>
                </>
              ) : (
                <>
                  {e.unique ? (
                    <>
                      <Button onClick={() => formPopup(e.type, e.key)}>{t('pay_rolls.manual.button.adjust')}</Button>
                    </>
                  ) : (
                    <b>{t('pay_rolls.manual.pre_exists')}</b>
                  )}
                </>
              )}
            </Col>
          </Row>
        )
      })}
      <Row>
        <Button onClick={save} size="extra-extra-large" type="primary">
          {t('pay_rolls.manual.button.save_changes')}
        </Button>
      </Row>

      <Modal
        visible={modalVisible}
        onCancel={() => {
          setPopContent('')
          setModalVisible(false)
        }}
        width={
          popContent === 'car' && periodContract?.contract.carAllowanceRegistrationMethodType === 'Detailed' ? 978 : 582
        }
        footer={null}
      >
        <PayRollAddEmployeePopup
          content={popContent}
          adjustKey={adjustKey}
          oldValue={oldValue}
          period={props.period}
          periodContract={periodContract}
          employeeID={employeeID}
          addAdjustment={addAdjustment}
          employee={props.employee}
          company={props.company}
          oneTimePays={props.oneTimePays}
          salaryCycle={props.chosenSalaryCycle}
          supplementBalances={supplementBalances.supplementBalances}
          supplementTypes={props.supplementTypes.supplementTypes}
          leaveBalances={leaveBalances.leaveBalances}
          leaveTypes={props.leaveTypes.leaveTypes}
          costCenters={costCenters.costCenters}
          user={props.user}
          carAllowance={props.carAllowances}
          departments={props.departments.departments}
          timeRegistrations={props.timeRegistrations}
          pensionCompanies={props.pensionCompanies.pensionCompanies}
          oneTimePayPensions={props.oneTimePayPension}
          viewingContract={props.employee?.activeContract}
          contractDelta={[...employeeContractDeltas.contracts]}
          coarseTimeRegistrations={props.coarseTimeRegistrations}
          chosenSalaryCycle={props.chosenSalaryCycle}
          chosenSalaryCycleID={props.chosenSalaryCycleID}
          chosenPeriod={props.chosenPeriod}
          chosenPeriodID={props.chosenPeriodID}
          salaryTypes={salaryTypes.salaryTypes}
          coarseSalaryRegistrations={props.coarseSalaryRegistrations}
          salaryRegistrations={props.salaryRegistrations}
          projects={props.projects.projects}
        />
      </Modal>
    </>
  )
}

export default connectToReducer<Reducers, Actions, Props>(
  (state) => ({
    salaryCycles: state.salaryCycles,
    oneTimePays: state.oneTimePays,
    leaveBalances: state.leaveBalances,
    departments: state.departments,
    timeRegistrations: state.timeRegistrations,
    carAllowances: state.carAllowances,
    coarseTimeRegistrations: state.coarseTimeRegistrations,
    supplementBalances: state.supplementBalances,
    supplementTypes: state.supplementTypes,
    leaveTypes: state.leaveTypes,
    costCenters: state.costCenters,
    user: state.user,
    pensionCompanies: state.pensionCompanies,
    oneTimePayPension: state.oneTimePayPensions,
    employeeContractDeltas: state.employeeContractDeltas,
    salaryTypes: state.salaryTypes,
    coarseSalaryRegistrations: state.coarseSalaryRegistrations,
    salaryRegistrations: state.salaryRegistrations,
    projects: state.projects,
    paySlips: state.paySlips,
    coarseCarAllowance: state.coarseCarAllowances,
  }),
  {
    getSalaryCycles: getSalaryCycles,
    getOneTimePays: getOneTimePays,
    getLeaveBalances: getLeaveBalances,
    getTimeRegistrations: getTimeRegistrations,
    getCarAllowances: getCarAllowances,
    getCoarseTimeRegistrations: getCoarseTimeRegistrations,
    getSupplementBalances: getSupplementBalances,
    getLeaveTypes: getLeaveTypes,
    getCostCenters: getCostCenters,
    getPensionCompanies: getPensionCompanies,
    getOneTimePayPensions: getOneTimePayPensions,
    getCoarseSalaryRegistrations: getCoarseSalaryRegistrations,
    getSalaryRegistrations: getSalaryRegistrations,
    getContractDeltas: getContractDeltas,
    getRemuneration: getRemuneration,
    getProjects: getProjects,
    getPaySlips: getPaySlips,
    getCoarseCarAllowances: getCoarseCarAllowances,
  }
)(PayRollAddSingleEmployee)
